import React, { FC, Fragment, useState, Suspense } from 'react';
import { Paper, Table, TableCell, TableContainer, TableHead, TableRow, TableBody, Box, Button } from '@mui/material';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { getPatientOrdersByMatBatchNumTemplate } from 'apollo/templates/patient';
import { PatientOrders } from 'models/patient';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import PatientOrdersItem from 'components/patient-history/patient-orders-item.component';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import PatientsPageActions from 'components/patient-history/patients-page-actions';
import { Helmet } from 'react-helmet-async';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

const PatientByMatBatchList: FC = () => {
    const { t: translate } = useTranslation(['common', 'patient']);
    const [lastOffset, setLastOffset] = useState(0);
    const [patientOrdersList, setPatientOrdersList] = useState<PatientOrders[]>([]);
    const [searchText, setSearchText] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    const [getPatientOrdersByMatBatchNum, { loading, fetchMore, networkStatus }] = useLazyQuery(
        getPatientOrdersByMatBatchNumTemplate,
        {
            notifyOnNetworkStatusChange: true,
            onCompleted: ({ patientOrdersByMatBatchNum }) => {
                setPatientOrdersList(patientOrdersByMatBatchNum);
                setLastOffset(patientOrdersByMatBatchNum.length);
            },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    const handleFilterChange = (searchText: string) => {
        setSearchText(searchText);
    };

    const getPatients = () => {
        if (searchText.length > 2) {
            setLastOffset(0);
            getPatientOrdersByMatBatchNum({
                variables: {
                    searchText: searchText,
                    limit: 25,
                    offset: 0,
                },
            });
        }
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, patientOrdersList?.length || 0);

    return (
        <Fragment>
            <Helmet>
                <title>{translate('patient:MAT_BATCH')} | Denthub</title>
            </Helmet>
            <Box display="flex">
                <PatientsPageActions
                    label={translate('patient:SEARCH_PATIENT')}
                    placeholder={translate('patient:MAT_BATCH_NUM_PLACEHOLDER')}
                    onFilterChange={handleFilterChange}
                />
                <Button disabled={searchText.length < 3} onClick={getPatients}>
                    {translate('patient:SEARCH')}
                </Button>
            </Box>
            {!loading || networkStatus === NetworkStatus.fetchMore ? (
                <Fragment>
                    <Suspense fallback={null}>
                        <TableContainer component={Paper}>
                            <InfiniteScroll
                                dataLength={patientOrdersList ? patientOrdersList.length : 0}
                                next={handleFetchMore}
                                hasMore={true}
                                throttleTimeInMs={75}
                                loader={undefined}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{translate('common:NAME')}</TableCell>
                                            <TableCell>{translate('common:PERSONAL_IDENTITY_NUMBER')}</TableCell>
                                            <TableCell>{translate('patient:PATIENT_CATEGORY')}</TableCell>
                                            <TableCell>{translate('patient:NUMBER_OF_ORDERS')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {searchText.length > 2 ? (
                                            <Fragment>
                                                {patientOrdersList.map((patientOrders: PatientOrders, index) => (
                                                    <PatientOrdersItem key={index} patientOrders={patientOrders} />
                                                ))}
                                            </Fragment>
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                                                    {translate('patient:PATINET_LIST_INSTRUCTIONS')}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </InfiniteScroll>
                        </TableContainer>
                    </Suspense>
                </Fragment>
            ) : (
                <Loader />
            )}
        </Fragment>
    );
};

export default PatientByMatBatchList;
